/*
 * Location Picker Component
 * 
 * Interactive map component for selecting pickup locations in route subscriptions
 * Allows users to click on map to set their preferred pickup location
 * 
 * FEATURES:
 * - Click-to-place location marker
 * - Current location detection
 * - Visual feedback with purple marker
 * - Coordinates display in popup
 * 
 * TO EDIT LOCATION PICKER:
 * - Marker style: Update locationPickerIcon in utils/config.js
 * - Default behavior: Modify click handling in useMapEvents
 * - Popup content: Update the Popup component content
 */

import React from 'react';
import { Marker, Popup, useMapEvents } from 'react-leaflet';
import { MARKERS } from '../../utils/config';

/**
 * Location Picker Component
 * Interactive map component for route subscription location selection
 * 
 * @param {Object} position - Current selected position {lat, lng}
 * @param {Function} onLocationSelect - Callback when location is selected
 */
const LocationPicker = ({ position, onLocationSelect }) => {
  // Handle map click events to set pickup location
  const map = useMapEvents({
    click(e) {
      // Call the parent component's location selection handler
      onLocationSelect(e.latlng);
    },
  });

  // Only render marker if position is set
  return position ? (
    <Marker position={position} icon={MARKERS.locationPickerIcon}>
      <Popup>
        <div className="text-sm">
          <h3 className="font-bold text-purple-800 mb-2">📍 Pickup Location</h3>
          <div className="space-y-1">
            <p><strong>Latitude:</strong> {position.lat.toFixed(6)}</p>
            <p><strong>Longitude:</strong> {position.lng.toFixed(6)}</p>
          </div>
          <div className="mt-3 pt-2 border-t border-purple-200">
            <p className="text-xs text-purple-600">
              💡 Click anywhere on the map to change location
            </p>
          </div>
        </div>
      </Popup>
    </Marker>
  ) : null;
};

export default LocationPicker;